// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  IAssessmentCompetencyUnitGetDetailAttrs,
  IAssessmentCompetencyUnitDownloadDocumentAttrs,
} from 'interfaces/back-office/assessment/registration/competency-unit/competency-unit-view.interface'
import {
  IAssessmentCompetencyUnitApiDetailResponse,
  IAssessmentCompetencyUnitApiDownloadDocumentResponse,
} from 'interfaces/back-office/assessment/registration/competency-unit/competency-unit-api.interface'
import { ISharedCommonLoadingAttrs, ISharedCommonLoadingQuestionSetAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Utils
import { withHelper_fileDownload } from 'models/extensions/with-helper'
import {
  withHelper_competencyUnitQuestionSetModel,
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_commonMapQuestionSet,
  withHelper_commonMapQuestionSetLoading,
} from 'models/extensions/helper'

// Constant
import { LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

import contentDisposition from 'content-disposition'

// Types
const { optional, model } = types

// Loading
const { DETAIL, DOWNLOAD } = LOADING
const { ANSWER_FILES } = LOADING_KEY_TYPE

// Base Model
const AssessmentCompetencyUnitStoreModel = model('AssessmentCompetencyUnit')
  .props({
    assessmentCompetencyUnit_loading: withHelper_commonLoadingModel,
    assessmentCompetencyUnit_detail: optional(withHelper_competencyUnitQuestionSetModel, {}),
  })
  .actions(self => ({
    assessmentCompetencyUnit_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.assessmentCompetencyUnit_loading, payload)

      detach(self.assessmentCompetencyUnit_loading)
      self.assessmentCompetencyUnit_loading = cast(map)
    },
    assessmentCompetencyUnit_SET_DETAIL: (payload: IAssessmentCompetencyUnitApiDetailResponse['res']['data']): void => {
      const questionSet = withHelper_commonMapQuestionSet(payload.questionSet)
      const map = cloneDeep({
        ...payload,
        questionSet,
      })

      detach(self.assessmentCompetencyUnit_detail)
      self.assessmentCompetencyUnit_detail = cast(map)
    },
    assessmentCompetencyUnit_MAP_LOADING_DETAIL: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const questionSet = withHelper_commonMapQuestionSetLoading(self.assessmentCompetencyUnit_detail.questionSet, payload)
      const map = cloneDeep({
        ...self.assessmentCompetencyUnit_detail,
        questionSet,
      })

      detach(self.assessmentCompetencyUnit_detail)
      self.assessmentCompetencyUnit_detail = cast(map)
    },
    assessmentCompetencyUnit_CLEAR_DETAIL: (): void => {
      self.assessmentCompetencyUnit_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch assessment unit competency detail
     *
     * @param {IAssessmentCompetencyUnitGetDetailAttrs} payload
     *
     * @return {any} any
     */
    assessmentCompetencyUnit_fetchDetail: flow(function* (payload: IAssessmentCompetencyUnitGetDetailAttrs) {
      self.assessmentCompetencyUnit_SET_LOADING({ type: DETAIL, value: true })

      const response: IAssessmentCompetencyUnitApiDetailResponse =
        yield self.environment.assessmentCompetencyUnitApi.assessmentCompetencyUnit_getDetail({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        self.assessmentCompetencyUnit_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.assessmentCompetencyUnit_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Download assessment unit competency document
     *
     * @param {IAssessmentCompetencyUnitDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    assessmentCompetencyUnit_downloadDocument: flow(function* (payload: IAssessmentCompetencyUnitDownloadDocumentAttrs) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.assessmentCompetencyUnit_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: true,
      })

      const response: IAssessmentCompetencyUnitApiDownloadDocumentResponse =
        yield self.environment.assessmentCompetencyUnitApi.assessmentCompetencyUnit_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        })

      if (response.ok) {
        const _contentDisposition = response.headers?.["content-disposition"] || ""
        const filename = contentDisposition.parse(_contentDisposition).parameters?.filename || "";
        const fileType = filename.split(".")[filename.split(".").length - 1];
        let mimeType = "";

        if (fileType === "pdf") {
          mimeType = 'application/pdf';
        } else if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
          mimeType = `image/${fileType}`;
        } else {
          withHelper_fileDownload(response.res, response.headers!);
          // Do when response is success
          if (payload?.onSuccess) payload.onSuccess() ;
          return;
        }
        
        const file = new File([response.res], filename, {
          type: mimeType
        });
        const url = URL.createObjectURL(file);
        window.open(url);

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Start Loading
      self.assessmentCompetencyUnit_MAP_LOADING_DETAIL({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: false,
      })
    }),
  }))

export const createAssessmentCompetencyUnitStoreDefaultModel = () => optional(AssessmentCompetencyUnitStoreModel, {})
